import type {
  CartItemResponse,
  OrderResponse,
  ProductResponse,
  VariationResponse,
} from '~/types/ecommerce';

declare module '~/plugins/bus' {
  /**
   * Defines the structure of custom events emitted and listened to via the event bus.
   */
  export interface Events {
    /**
     * Triggered when a product is viewed.
     * @param product The product being viewed.
     * @param variation Optional variation of the product.
     */
    'product:view': (product: ProductResponse, variation?: VariationResponse) => void;

    /**
     * Triggered when a product is added to the cart.
     * @param product The product being added.
     * @param variation Optional variation of the product.
     * @param quantity Optional quantity to add.
     */
    'product:add': (product: ProductResponse, variation?: VariationResponse, quantity?: number) => void;

    /**
     * Triggered when items are removed from the cart.
     * @param items The list of cart items removed.
     */
    'cart:remove': (items: CartItemResponse[]) => void;

    /**
     * Triggered when a user registers.
     */
    'auth:register': () => void;

    /**
     * Triggered when a user logs in.
     */
    'auth:login': () => void;

    /**
     * Triggered when a user logs out.
     */
    'auth:logout': () => void;

    /**
     * Triggered when the checkout process starts.
     * @param items The list of cart items being checked out.
     */
    'checkout:start': (items: CartItemResponse[]) => void;

    /**
     * Triggered when the checkout process starts.
     * @param items The list of cart items being checked out.
     */
    'checkout:cancel': (items: CartItemResponse[]) => void;

    /**
     * Triggered when payment is initiated during checkout.
     * @param quoteId The identifier of the quote being paid.
     */
    'checkout:pay': (quoteId: string) => void;

    /**
     * Triggered when checkout is completed.
     * @param payload The list of orders created from the checkout.
     */
    'checkout:complete': (payload: OrderResponse[]) => void;
  }
}

export default defineNuxtPlugin({
  name: 'kygunco:ecommerce',
  dependsOn: [],
  parallel: true,
  setup: () => {
    const config = useRuntimeConfig();

    const fetch = $fetch.create({
      baseURL: config.public.ecommerce.api.url,
      retry: 5,
      retryDelay: 1000,
      retryStatusCodes: [429],
      onRequest: async ({ options }) => {
        const authStore = useAuthStore();

        const headers = new Headers(options.headers);

        const _ = authStore.isValid
          ? headers.set('Authorization', `Bearer ${authStore.accessToken}`)
          : headers.delete('Authorization');

        options.headers = headers;
      },
    });

    return {
      provide: {
        ecommerce: {
          fetch,
        },
      },
    };
  },
});
